import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Cause,
  Issue,
  Machine,
  Routine,
  SuggestionStatus,
  Template,
  QueryParams,
  YesOrNo,
} from '../types';
import {
  getCausesByTemplateId,
  getIssuesByTemplateId,
  getMachinesByTemplateId,
  getRoutineByTemplateId,
  getTemplateById,
  getTemplates,
} from '../utils/httpRequest';
import { RootState } from '../store';
import { groupBy } from 'lodash';
import { mapSolutionGrid } from '../utils/dataMapper';

export interface TemplateState {
  templates: Template[];
  linkedMachines?: Machine[];
  template?: Template;
  draftTemplate?: Template;
  issues?: Issue[];
  causes?: Cause[];
  routines?: Routine[];
  loading: boolean;
  isLoadingLinkedMachine?: boolean;
  isLoadingRoutine?: boolean;
  isLoadingIssue?: boolean;
  isLoadingCause?: boolean;
  error?: string;
}

const initialState: TemplateState = {
  templates: [],
  loading: false,
  isLoadingRoutine: false,
};

export const fetchTemplates = createAsyncThunk(
  'templates/fetchTemplates',
  async (params?: QueryParams) => {
    const templates = await getTemplates(params);

    return templates;
  }
);

export const fetchTemplateById = createAsyncThunk(
  'templates/fetchTemplateById',
  async (id: string) => {
    const { data } = await getTemplateById(id);

    return data;
  }
);

export const fetchLinkedMachines = createAsyncThunk(
  'templates/fetchLinkedMachines',
  async (templateId: string) => {
    const { data } = await getMachinesByTemplateId(templateId);

    return data;
  }
);

export const fetchTemplateIssues = createAsyncThunk(
  'templates/fetchTemplateIssues',
  async ({
    templateId,
    params,
  }: {
    templateId: string;
    params?: QueryParams;
  }) => {
    const { data } = await getIssuesByTemplateId(templateId, params);

    return data;
  }
);

export const fetchTemplateCauses = createAsyncThunk(
  'templates/fetchTemplateCauses',
  async ({
    templateId,
    params,
  }: {
    templateId: string;
    params?: QueryParams;
  }) => {
    const { data } = await getCausesByTemplateId(templateId, params);

    return data;
  }
);

export const fetchTemplateRoutines = createAsyncThunk(
  'templates/fetchTemplateRoutines',
  async ({
    templateId,
    params,
  }: {
    templateId: string;
    params?: QueryParams;
  }) => {
    const { data } = await getRoutineByTemplateId(templateId, params);

    return data;
  }
);

export const templateSlice = createSlice({
  name: 'template',
  initialState,
  reducers: {
    createDraftTemplate: (
      state,
      action: PayloadAction<Template | undefined>
    ) => {
      state.draftTemplate = action.payload?.templateID
        ? { ...action.payload }
        : {
            templateID: '',
            name: '',
            type: '',
            machines: [],
            issues: [],
            causes: [],
            solutionGrid: [],
          };
    },
    updateDraftTemplate: (state, action: PayloadAction<Template>) => {
      state.draftTemplate = {
        ...state.draftTemplate,
        ...action.payload,
      };
    },
    setTemplates: (state, action: PayloadAction<Template[]>) => {
      state.templates = action.payload;
    },
    addTemplate: (state, action: PayloadAction<Template>) => {
      state.templates = [action.payload, ...state.templates];
    },
    removeTemplate: (state, action: PayloadAction<string>) => {
      state.templates = state.templates.filter(
        ({ templateID }) => templateID !== action.payload
      );
    },
    setTemplateCauses: (
      state,
      action: PayloadAction<{
        causes: Cause[];
      }>
    ) => {
      state.causes = action.payload.causes;
    },
    setTemplateIssues: (
      state,
      action: PayloadAction<{
        issues: Issue[];
      }>
    ) => {
      state.issues = action.payload.issues;
    },
    linkIssueToTemplate: (
      state,
      action: PayloadAction<{
        issue: Issue;
      }>
    ) => {
      state.issues = [...(state.issues || []), action.payload.issue];
    },
    linkCauseToTemplate: (
      state,
      action: PayloadAction<{
        id?: string;
        cause: Cause;
      }>
    ) => {
      state.causes = [...(state.causes || []), action.payload.cause];
    },
    setLinkedMachines: (state, action: PayloadAction<Machine[]>) => {
      state.linkedMachines = action.payload;
    },
    resetTemplate: (state) => {
      state.template = initialState.template;
      state.issues = [];
      state.causes = [];
      state.linkedMachines = [];
      state.routines = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTemplates.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.templates = action.payload;
        state.loading = false;
        state.error = '';
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.error = 'Fetch Template list failed.';
        state.loading = false;
      })
      .addCase(fetchTemplateById.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchTemplateById.fulfilled, (state, action) => {
        state.template = {
          ...state.template,
          ...action.payload,
        };
        state.draftTemplate = {
          ...state.draftTemplate,
          ...action.payload,
        };
        state.loading = false;
        state.error = '';
      })
      .addCase(fetchTemplateById.rejected, (state, action) => {
        state.error = 'Fetch Template list failed.';
        state.loading = false;
      })
      .addCase(fetchLinkedMachines.pending, (state) => {
        state.isLoadingLinkedMachine = true;
        state.error = '';
      })
      .addCase(fetchLinkedMachines.fulfilled, (state, action) => {
        state.linkedMachines = action.payload || [];
        state.isLoadingLinkedMachine = false;
        state.error = '';
      })
      .addCase(fetchLinkedMachines.rejected, (state, action) => {
        state.error = 'Fetch linked machines failed.';
        state.isLoadingLinkedMachine = false;
      })
      .addCase(fetchTemplateIssues.pending, (state) => {
        state.isLoadingIssue = true;
        state.error = '';
      })
      .addCase(fetchTemplateIssues.fulfilled, (state, action) => {
        state.issues = action.payload;
        state.isLoadingIssue = false;
        state.error = '';
      })
      .addCase(fetchTemplateIssues.rejected, (state, action) => {
        state.error = 'Fetch template issues failed.';
        state.isLoadingIssue = false;
      })
      .addCase(fetchTemplateCauses.pending, (state) => {
        state.isLoadingCause = true;
        state.error = '';
      })
      .addCase(fetchTemplateCauses.fulfilled, (state, action) => {
        state.causes = action.payload;
        state.isLoadingCause = false;
        state.error = '';
      })
      .addCase(fetchTemplateCauses.rejected, (state, action) => {
        state.error = 'Fetch template causes failed.';
        state.isLoadingCause = false;
      })
      .addCase(fetchTemplateRoutines.pending, (state) => {
        state.isLoadingRoutine = true;
        state.error = '';
      })
      .addCase(fetchTemplateRoutines.fulfilled, (state, action) => {
        state.routines = action.payload;
        state.isLoadingRoutine = false;
        state.error = '';
      })
      .addCase(fetchTemplateRoutines.rejected, (state, action) => {
        state.error = 'Fetch template routines failed.';
        state.isLoadingRoutine = false;
      });
  },
});

export const {
  setTemplates,
  addTemplate,
  removeTemplate,
  createDraftTemplate,
  updateDraftTemplate,
  setTemplateIssues,
  setTemplateCauses,
  linkIssueToTemplate,
  linkCauseToTemplate,
  setLinkedMachines,
  resetTemplate,
} = templateSlice.actions;

export default templateSlice.reducer;

export const selectTemplates = (state: RootState) => state.templates.templates;

export const selectDraftTemplate = (state: RootState) =>
  state.templates.draftTemplate;

export const selectDraftTemplateId = (state: RootState) =>
  state.templates.draftTemplate?.templateID || '';

export const selectGroupedTemplates = (state: RootState) => {
  const { templates } = state.templates;
  return groupBy(templates, 'type');
};

export const selectMappedSolutionGrid = (state: RootState) => {
  const { solutionGrid } = state.templates.draftTemplate || {};
  if (!solutionGrid) {
    return [];
  }
  return mapSolutionGrid(solutionGrid);
};

export const selectRoutines = (state: RootState) =>
  state.templates.routines || [];

export const selectTemplateRoutines = (state: RootState) =>
  state.templates.routines || [];

export const selectIsFetchingTemplates = (state: RootState) =>
  state.templates.loading;

export const selectIsFetchingTemplateRoutines = (state: RootState) =>
  state.templates.isLoadingRoutine;

export const selectLinkedMachines = (state: RootState) =>
  state.templates.linkedMachines;

export const selectTemplateCurrentRoutines = (state: RootState) =>
  (state.templates.routines || []).filter(
    (i) =>
      i.isSuggestion === YesOrNo.NO ||
      i.suggestionStatus === SuggestionStatus.NONE ||
      i.suggestionStatus === SuggestionStatus.APPROVED
  );

export const selectTemplateToBeReviewedRoutines = (state: RootState) =>
  (state.templates.routines || []).filter(
    (i) =>
      i.isSuggestion === YesOrNo.YES &&
      i.suggestionStatus === SuggestionStatus.SUBMITTED
  );
export const selectTemplateRejectedRoutines = (state: RootState) =>
  (state.templates.routines || []).filter(
    (i) =>
      i.isSuggestion === YesOrNo.YES &&
      i.suggestionStatus === SuggestionStatus.REJECTED
  );

export const selectTemplateIssues = (state: RootState) =>
  state.templates.issues || [];

export const selectTemplateCurrentIssues = (state: RootState) =>
  (state.templates.issues || []).filter(
    (i) =>
      i.isSuggestion === YesOrNo.NO ||
      i.suggestionStatus === SuggestionStatus.NONE ||
      i.suggestionStatus === SuggestionStatus.APPROVED
  );

export const selectTemplateRejectedIssues = (state: RootState) =>
  (state.templates.issues || []).filter(
    (i) => i.suggestionStatus === SuggestionStatus.REJECTED
  );

export const selectTemplateToBeReviewedIssues = (state: RootState) =>
  (state.templates.issues || []).filter(
    (i) =>
      i.isSuggestion === YesOrNo.YES &&
      i.suggestionStatus === SuggestionStatus.SUBMITTED
  );

export const selectTemplateCauses = (state: RootState) =>
  state.templates.causes;

export const selectTemplateCurrentCauses = (state: RootState) =>
  (state.templates.causes || []).filter(
    (i) =>
      i.isSuggestion === YesOrNo.NO ||
      i.suggestionStatus === SuggestionStatus.NONE ||
      i.suggestionStatus === SuggestionStatus.APPROVED
  );

export const selectTemplateRejectedCauses = (state: RootState) =>
  (state.templates.causes || []).filter(
    (i) => i.suggestionStatus === SuggestionStatus.REJECTED
  );

export const selectTemplateToBeReviewedCauses = (state: RootState) =>
  (state.templates.causes || []).filter(
    (i) =>
      i.isSuggestion === YesOrNo.YES &&
      i.suggestionStatus === SuggestionStatus.SUBMITTED
  );
